import type { D20Face, SaveFace } from '@rookandroll/engine';

const FACE_COLOUR: Record<D20Face, { c: string; ink?: boolean }> = {
  '1': { c: 'var(--blue)' },
  '2': { c: 'var(--green)' },
  '3': { c: 'var(--yellow)', ink: true },
  skip: { c: 'var(--red)' },
  reverse: { c: 'var(--face-ink)' },
};

function Icon({ face }: { face: 'skip' | 'reverse' }) {
  return (
    <svg x="42" y="46" width="56" height="56" viewBox="0 0 40 40" aria-hidden="true" style={{ color: '#fff' }}>
      {face === 'skip' ? (
        <>
          <circle cx="20" cy="20" r="14" fill="none" stroke="currentColor" strokeWidth="6" />
          <path d="M10 30L30 10" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
        </>
      ) : (
        <path d="M8 16h22l-6-7M32 24H10l6 7" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

const DISPLAY = { fontFamily: "'Archivo Black', sans-serif" } as const;

/** The d20, drawn as a hexagon die. `face` is null before the first roll. */
export function Die({ face }: { face: D20Face | null }) {
  const d = face ? FACE_COLOUR[face] : null;
  const fill = d ? d.c : 'var(--face-ink)';
  const text = d?.ink ? '#121826' : '#fff';
  const label = `d20 showing ${face ?? 'nothing yet'}`;
  return (
    <svg className="die" viewBox="0 0 140 140" role="img" aria-label={label}>
      <polygon points="70,6 126,38 126,102 70,134 14,102 14,38" fill={fill} stroke="#fff" strokeWidth="4" strokeLinejoin="round" />
      <g fill="none" stroke={text} strokeOpacity=".35" strokeWidth="1.5">
        <polygon points="70,22 112,96 28,96" />
        <path d="M70 6V22M126 38L112 96M126 102L112 96M70 134L70 96M14 102L28 96M14 38L28 96" />
      </g>
      {face === null && (
        <text x="70" y="86" textAnchor="middle" fontSize="46" style={DISPLAY} fill="#fff">?</text>
      )}
      {face !== null && /^\d$/.test(face) && (
        <text x="70" y="88" textAnchor="middle" fontSize="58" fontStyle="italic" style={DISPLAY} fill={text}>{face}</text>
      )}
      {(face === 'skip' || face === 'reverse') && <Icon face={face} />}
    </svg>
  );
}

/** The six-sided save die. */
export function SaveDie({ face }: { face: SaveFace | null }) {
  const fill = face === 'save' ? 'var(--green)' : face === 'blank' ? '#e3e7ef' : 'var(--face-ink)';
  return (
    <svg className="sdie" viewBox="0 0 100 100" role="img" aria-label={`Save die showing ${face ?? 'nothing yet'}`}>
      <rect x="6" y="6" width="88" height="88" rx="18" fill={fill} stroke="#fff" strokeWidth="4" />
      {face === null && <text x="50" y="62" textAnchor="middle" fontSize="40" style={DISPLAY} fill="#fff">?</text>}
      {face === 'save' && (
        <>
          <path d="M50 24l20 8v14c0 14-9 23-20 27C39 69 30 60 30 46V32z" fill="#fff" />
          <path d="M40 47l7 7 13-14" fill="none" stroke="#1b9e5a" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {face === 'blank' && <circle cx="50" cy="50" r="5" fill="#9aa3b5" />}
    </svg>
  );
}
